/**
 * §P.9 fase 3 · El embudo del directorio.
 *
 * ## Los cuatro escalones son de la misma gente
 *
 * Y hubo que arreglar el servidor para que lo fueran: el cuarto contaba **todos** los
 * alumnos del club con seis meses de antigüedad, así que un club con tres pruebas
 * este mes y ciento veinte alumnos de antes leía «3 → 1 → 1 → 120». No fallaba nada y
 * se leía como un éxito, que es lo que lo hacía peligroso. Corregido el 19-09-2026 en
 * `PostgresReportRepository.funnel`.
 *
 * ## El último escalón no se mide contra el anterior
 *
 * Se mide contra `totals.enrolled_mature`: las altas que **ya han cumplido seis
 * meses**. Un alta de este mes no es una permanencia todavía ni es una baja, y
 * meterla en el denominador daría «0 % de permanencia» justo al club que acaba de
 * convertir diez. Por eso esa fila va separada y con su denominador escrito al lado:
 * un porcentaje cuyo denominador no es la fila de arriba tiene que decir cuál es.
 *
 * ## Sin pruebas no hay embudo, y se dice con lo que hay que hacer
 *
 * El vacío de este informe no es «no hay datos»: es «tu ficha no está publicada o
 * nadie la ha encontrado todavía». Lleva a la pantalla que lo arregla.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  etiquetaDeEscalon,
  type FilaDelEmbudo,
  type Informe,
} from '@/club/informes/tipos';

/** `0.333` → «33 %». Sin decimales: es una proporción, no una medición. */
const porcentaje = (v: number | null): string =>
  v === null ? '—' : `${Math.round(v * 100)} %`;

export function Embudo({
  club,
  alAbrirLaFicha,
}: {
  club: string;
  /** A «El club» → «Ficha del buscador», que es lo que arregla el vacío. */
  alAbrirLaFicha: () => void;
}) {
  return (
    <EnvolturaDeInforme<FilaDelEmbudo>
      club={club}
      clave="embudo"
      ruta="funnel"
      titulo="Embudo del directorio"
      explicacion={
        <>
          De la gente que te encuentra en el buscador: cuántos piden probar,
          cuántos vienen, cuántos se apuntan y{' '}
          <strong>cuántos siguen a los seis meses</strong>, que es el escalón
          que nadie mide y el único que dice si el club retiene.
        </>
      }
      vacio={{
        titulo: 'Todavía no ha pedido nadie una clase de prueba',
        explicacion:
          'El embudo sale de las reservas que llegan por tu ficha del buscador. Si no la has publicado, nadie puede reservar: está en «El club» → «Ficha del buscador».',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports.funnel.get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDelEmbudo>;
      }}
    >
      {(datos) => {
        const maduras = datos.totals.enrolled_mature ?? 0;
        const escalones = datos.rows.filter(
          (f) => f.stage !== 'siguen_a_los_6_meses'
        );
        const permanencia = datos.rows.find(
          (f) => f.stage === 'siguen_a_los_6_meses'
        );

        return (
          <>
            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Escalón</th>
                    <th className="num">Cuántos</th>
                    <th className="num">Del anterior</th>
                    <th className="num">Del total</th>
                  </tr>
                </thead>
                <tbody>
                  {escalones.map((f) => (
                    <tr key={f.stage}>
                      <td>{etiquetaDeEscalon(f.stage)}</td>
                      <td className="num">{f.count}</td>
                      <td className="num">
                        {porcentaje(f.rate_from_previous)}
                      </td>
                      <td className="num">{porcentaje(f.rate_from_start)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── La permanencia, aparte y con su denominador ────────────── */}
            {permanencia && (
              <section className="permanencia">
                <h2 className="seccion">Seguían a los seis meses</h2>

                {maduras === 0 ? (
                  /*
                   * Ni permanencia ni baja: todavía no se sabe. Decir «0 %» aquí
                   * sería la lectura contraria a la verdad, y es la que se copia a
                   * una reunión.
                   */
                  <p className="tenue">
                    Ninguna de tus altas ha cumplido todavía seis meses, así que
                    esta cifra no existe aún.{' '}
                    <strong>No es un cero: es que no se sabe.</strong> Vuelve
                    cuando la primera cumpla medio año.
                  </p>
                ) : (
                  <>
                    <dl className="datos">
                      <div>
                        <dt>Siguen</dt>
                        <dd>
                          {permanencia.count} de {maduras}
                        </dd>
                      </div>
                      <div>
                        <dt>Permanencia</dt>
                        <dd>{porcentaje(permanencia.rate_from_previous)}</dd>
                      </div>
                    </dl>

                    <p className="tenue">
                      Se mide sobre las <strong>{maduras}</strong> altas que ya
                      han cumplido seis meses, no sobre todas: las de este mes
                      todavía no han contestado a la pregunta.
                    </p>
                  </>
                )}
              </section>
            )}

            <p className="tenue">
              Solo cuenta quien entró por el buscador. Un alumno que llegó
              porque su prima entrena aquí no aparece en esta tabla — y está
              bien, es otra pregunta.{' '}
              <button
                type="button"
                className="enlace-fila"
                onClick={alAbrirLaFicha}
              >
                Ver tu ficha del buscador
              </button>
            </p>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
